import { useContext } from 'react'
import _ from 'lodash'

import { PassContext } from "../pass-context"
import dataSelector from './data-selector'
import PopupEditor from './popup-editor'
import { PassImage } from '/components/editor/pass-image'
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';


const PassListItem = () => {

  const { passType, subType, classData, objectData, variables } = useContext(PassContext)

  const passData = getPassTypeData({ passType, subType, classData, objectData, variables })

  const logoUrl = dataSelector({ objectData: passData.logoSource === "class" ? classData : objectData, variables, path: passData.logo })
  const wideLogoUrl = dataSelector({ objectData: passData.logoSource === "class" ? classData : objectData, variables, path: passData.wideLogo })

  const logoEditorSources = {
    source: passData.logoSource,
    logo: {
      path: passData.logo,
      value: logoUrl
    },
    wideLogo: {
      path: passData.wideLogo,
      value: wideLogoUrl
    }
  }

  return (
    <div className="google-list-item">
      <table width="100%">
        <tbody>
          <tr>
            <td align='right' valign="middle" width={70}>
              <PopupEditor sources={logoEditorSources} type="logo" logoType="logo" title="Logo Image">
                <div className="editable logo-editable ">
                  <div className='logoClipper'>
                    <PassImage src={logoUrl} className="logo" />
                  </div>
                </div>
              </PopupEditor>
            </td>

            <td valign="middle" className='pl-[14px] pt-[10px] pb-[10px]'>
              <div className="flex flex-col -gap-[3px]">

                <div className={'title' + (passData.titleEditable ? " editable" : "")}>
                  <PopupEditor title={passData.titleLabel} sources={[passData.titleSource]} path={passData.titlePath} disabled={!passData.titleEditable} required={passData.titleRequired} variable={passData.titleVariable}>
                    <div style={{ minWidth: 100, overflow: "wrap" }}>{passData.titleValue}</div>
                  </PopupEditor>
                </div>

                <div className={'body -mt-[8px]' + (passData.bodyEditable ? " editable" : "")}>
                  <PopupEditor title={passData.bodyLabel} sources={[passData.bodySource]} path={passData.bodyPath} disabled={!passData.bodyEditable} required={passData.bodyRequired} variable={passData.bodyVariable}>
                    <div style={{ minWidth: 100, overflow: "wrap" }}>{passData.bodyValue}</div>
                  </PopupEditor>
                </div>
              </div>
            </td>

            <td valign="middle" width={50}>
              <StarBorderRoundedIcon sx={{ fontSize: 28 }} />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

export default PassListItem

const getPassTypeData = ({ passType, subType, classData, objectData, variables }) => {
  switch (passType) {
    case "generic": return {
      logo: "logo.sourceUri.uri",
      wideLogo: "wideLogo.sourceUri.uri",
      logoSource: "object",
      titleEditable: true,
      titleRequired: true,
      titleLabel: "Title",
      titlePath: "cardTitle.defaultValue.value",
      titleSource: "object",
      titleValue: dataSelector({ objectData: objectData, variables, path: "cardTitle.defaultValue.value" }),
      titleVariable: "pass.title",
      bodyEditable: true,
      bodyRequired: true,
      bodyLabel: "Header",
      bodyPath: "header.defaultValue.value",
      bodySource: "object",
      bodyValue: dataSelector({ objectData: objectData, variables, path: "header.defaultValue.value" }),
      bodyVariable: "pass.header"
    }

    case "boardingPass": return {
      logo: "flightHeader.carrier.airlineLogo.sourceUri.uri",
      wideLogo: "flightHeader.carrier.airlineWideLogo.sourceUri.uri",
      logoSource: "class",
      titleEditable: false,
      titleLabel: "",
      titlePath: "",
      titleSource: "class",
      titleValue: "",
      titleVariable: "pass.title",
      bodyEditable: false,
      bodyLabel: "",
      bodyPath: "",
      bodySource: "class",
      bodyValue: "",
      bodyVariable: ""
    }

    case "transit": return {
      logo: "logo.sourceUri.uri",
      wideLogo: "wideLogo.sourceUri.uri",
      logoSource: "object",
      titleEditable: false,
      titleLabel: "Journey",
      titlePath: "",
      titleValue: getTransitTitle({ subType, variables }),
      titleSource: "object",
      titleVariable: "pass.title",
      bodyEditable: false,
      bodyLabel: "Departure Date",
      bodyPath: "",
      bodySource: "object",
      bodyValue: getTransitBody({ subType, variables }),
      bodyVariable: ""
    }

    case "offer": return {
      logo: "titleImage.sourceUri.uri",
      wideLogo: "wideTitleImage.sourceUri.uri",
      logoSource: "class",
      titleEditable: true,
      titleRequired: true,
      titleLabel: "Issuer Name",
      titlePath: "issuerName",
      titleSource: "class",
      titleValue: dataSelector({ objectData: classData, variables, path: "issuerName" }),
      titleVariable: "pass.issuer_name",
      bodyEditable: true,
      bodyRequired: true,
      bodyLabel: "Title",
      bodyPath: "title",
      bodySource: "class",
      bodyValue: dataSelector({ objectData: classData, variables, path: "title" }),
      bodyVariable: "pass.title"
    }

    case "giftCard": return {
      logo: "programLogo.sourceUri.uri",
      wideLogo: "wideProgramLogo.sourceUri.uri",
      logoSource: "class",
      titleEditable: true,
      titleRequired: true,
      titleLabel: "Balance",
      titlePath: "balance.micros",
      titleSource: "object",
      titleValue: getGiftCardBody({ objectData, variables }),
      titleVariable: "gift.points.balance",
      bodyLabel: "Issuer Name",
      bodyPath: "issuerName",
      bodySource: "class",
      bodyValue: dataSelector({ objectData: classData, variables, path: "issuerName" }),
      bodyEditable: true,
      bodyRequired: true,
      bodyVariable: "pass.issuer_name"
    }
    case "loyalty": return {
      logo: "programLogo.sourceUri.uri",
      wideLogo: "wideProgramLogo.sourceUri.uri",
      logoSource: "class",
      titleEditable: true,
      titleRequired: true,
      titleLabel: "Issuer Name",
      titlePath: "issuerName",
      titleSource: "class",
      titleValue: dataSelector({ objectData: classData, variables, path: "issuerName" }),
      titleVariable: "pass.issuer_name",
      bodyEditable: true,
      bodyRequired: true,
      bodyLabel: "Program Name",
      bodyPath: "programName",
      bodySource: "class",
      bodyValue: dataSelector({ objectData: classData, variables, path: "programName" }),
      bodyVariable: "pass.program_name"
    }
    case "eventTicket": return {
      logo: "logo.sourceUri.uri",
      wideLogo: "wideLogo.sourceUri.uri",
      logoSource: "class",
      titleEditable: true,
      titleRequired: true,
      titleLabel: "Event Name",
      titlePath: "eventName.defaultValue.value",
      titleSource: "class",
      titleValue: dataSelector({ objectData: classData, variables, path: "eventName.defaultValue.value" }),
      titleVariable: "event.name",
      bodyLabel: "Issuer Name",
      bodyEditable: true,
      bodyRequired: true,
      bodyPath: "issuerName",
      bodySource: "class",
      bodyValue: dataSelector({ objectData: classData, variables, path: "issuerName" }),
      bodyVariable: "pass.issuer_name"
    }
  }
}

const getTransitTitle = ({ subType, variables }) => {
  switch (subType) {
    case "RAIL": {
      const origin = variables.journey.origin.code
      const destination = variables.journey.destination.code
      return `${origin} to ${destination}`
    }
  }
}

const getTransitBody = ({ subType, variables }) => {
  switch (subType) {
    case "RAIL": {
      return variables.journey.depart.date
    }
  }
}

const getGiftCardBody = ({ objectData, variables }) => {
  const resolvedBalance = dataSelector({ objectData, variables, path: "balance" })
  const microsString = Number(resolvedBalance.micros.toString().replace(/[^0-9.-]+/g, ""));
  return "Gift Card: " + microsString.toLocaleString('en-US', { style: 'currency', currency: resolvedBalance.currencyCode, minimumFractionDigits: 0 })
}
